import * as React from 'react';
import styled from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';

export interface SectionProps extends React.HTMLAttributes<HTMLElement> {
  title?: string;
  description?: string;
  actions?: React.ReactNode;
  bordered?: boolean;
  semanticId?: string;
}

const StyledSection = styled.section`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  font-family: var(--ui-font-sans);
`;

const StyledHeader = styled.div`
  margin-bottom: ${rem(25)};
`;

const StyledTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${rem(12)};
`;

const StyledTitle = styled.h3`
  margin: 0 0 ${rem(5)} 0;
  font-family: var(--ui-font-heading, var(--ui-font-sans));
  font-size: ${rem(20)};
  line-height: ${rem(26)};
  color: var(--ui-text);
`;

const Description = styled.span`
  font-family: var(--ui-font-sans);
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  color: var(--ui-text-muted);
`;

const BorderedWrapper = styled.div`
  padding-bottom: ${rem(24)};
  border-bottom: 1px solid var(--ui-border-muted);
`;

const Body = styled.div``;

export const Section = React.forwardRef<HTMLElement, SectionProps>(function Section(
  { title, description, actions, bordered = false, semanticId, children, ...rest },
  ref
) {
  const semanticProps = getSemanticAttributes(semanticId);
  const hasHeader =
    (title != null && title !== '') ||
    (description != null && description !== '') ||
    actions != null;

  const content = (
    <StyledSection ref={ref} {...semanticProps} {...rest}>
      {hasHeader && (
        <StyledHeader>
          <StyledTitleRow>
            {title != null && title !== '' ? <StyledTitle>{title}</StyledTitle> : null}
            {actions != null ? <div>{actions}</div> : null}
          </StyledTitleRow>
          {description != null && description !== '' ? (
            <Description>{description}</Description>
          ) : null}
        </StyledHeader>
      )}
      <Body>{children}</Body>
    </StyledSection>
  );

  if (bordered) {
    return <BorderedWrapper>{content}</BorderedWrapper>;
  }

  return content;
});

Section.displayName = 'Section';
